import React, { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import { VIDEO_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import useVideoKeyboard from './useVideoKeyboard';
import { applyQuality, buildQualityList } from './utils/videoQualityUtils';

interface Options {
    url: string;
    thumbnailUrl?: string;
    onClose?: () => void;
    /** Client gallery event bus, injected via react2angular. When present, the player pauses on
     * VIDEO_EVENTS.STOP — the lightbox filmstrip keeps navigated-away videos mounted, so the
     * unmount cleanup below never fires on next/prev and can't be relied on to stop playback. */
    spEventBus?: SpAngularJs.ISpEventBus;
}

const VIDEO_MIME_TYPE = 'application/x-mpegURL';

const useVideoPlayer = ({ url, thumbnailUrl, onClose, spEventBus }: Options) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
    const hasStartedRef = useRef(false);
    const isEndedRef = useRef(false);
    const isFocusedRef = useRef(false);
    const isFirstMountRef = useRef(true);

    const [hasStarted, setHasStarted] = useState(false);
    const [isEnded, setIsEnded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [quality, setQuality] = useState('Auto');
    const [qualities, setQualities] = useState<string[]>(['Auto']);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }

        const videoEl = document.createElement('video');
        videoEl.className = 'video-js';
        videoRef.current.appendChild(videoEl);

        const player = videojs(videoEl, {
            controls: false,
            fluid: true,
            aspectRatio: '16:9',
            poster: thumbnailUrl || undefined,
            sources: [{ src: url, type: VIDEO_MIME_TYPE }],
            preload: 'none',
            userActions: { click: false }
        });

        player.on('play', () => {
            hasStartedRef.current = true;
            isEndedRef.current = false;
            setHasStarted(true);
            setIsPlaying(true);
            setIsEnded(false);
        });

        player.on('pause', () => setIsPlaying(false));

        player.on('ended', () => {
            isEndedRef.current = true;
            setIsPlaying(false);
            setIsEnded(true);
        });

        player.on('timeupdate', () => setCurrentTime(player.currentTime() ?? 0));

        player.on('loadedmetadata', () => {
            setDuration(player.duration() ?? 0);
            setQualities(buildQualityList(player));
        });

        player.on('volumechange', () => {
            setIsMuted(player.muted() ?? false);
            setVolume(player.volume() ?? 1);
        });

        playerRef.current = player;

        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            player.dispose();
            playerRef.current = null;
        };
    }, []);

    // Pause on lightbox navigation. The filmstrip keeps navigated-away videos mounted, so the
    // unmount cleanup above doesn't fire on next/prev — this event is the only reliable stop.
    useEffect(() => {
        if (!spEventBus) {
            return;
        }

        return spEventBus.on(VIDEO_EVENTS.STOP, () => {
            playerRef.current?.pause();
        });
    }, [spEventBus]);

    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }

        if (hasStartedRef.current) {
            return;
        }

        if (playerRef.current) {
            playerRef.current.src([{ src: url, type: VIDEO_MIME_TYPE }]);
            playerRef.current.load();
            playerRef.current.poster(thumbnailUrl || '');
            isEndedRef.current = false;
            setHasStarted(false);
            setIsEnded(false);
            setCurrentTime(0);
            setIsPlaying(false);
            setDuration(0);
            setPlaybackSpeed(1);
            setQuality('Auto');
            setQualities(['Auto']);
        }
    }, [url, thumbnailUrl]);

    const handleFocus = () => {
        isFocusedRef.current = true;
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            isFocusedRef.current = false;
        }
    };

    const handlePlayPause = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.paused() ? p.play() : p.pause();
    };

    const handleSeek = (time: number) => {
        playerRef.current?.currentTime(time);
        setCurrentTime(time);
    };

    const handleToggleMute = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.muted(!p.muted());
    };

    const handleVolumeChange = (value: number) => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.volume(value);

        if (value > 0) {
            p.muted(false);
        }
    };

    const handleToggleFullscreen = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        if (document.fullscreenElement === containerRef.current) {
            document.exitFullscreen();
        } else if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
        }
    }, []);

    useVideoKeyboard({
        playerRef,
        hasStartedRef,
        isEndedRef,
        isFocusedRef,
        onToggleFullscreen: handleToggleFullscreen,
        onClose
    });

    const handleSpeedChange = (speed: number) => {
        playerRef.current?.playbackRate(speed);
        setPlaybackSpeed(speed);
        setShowSettings(false);
    };

    const handleQualityChange = (q: string) => {
        if (playerRef.current) {
            applyQuality(playerRef.current, q);
        }

        setQuality(q);
        setShowSettings(false);
    };

    const handleReplay = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        hasStartedRef.current = false;
        isEndedRef.current = false;
        setIsEnded(false);
        setCurrentTime(0);
        p.currentTime(0);
        p.play();
    };

    const getCurrentTime = useCallback(() => Number(playerRef.current?.currentTime() ?? 0), []);

    const play = useCallback(() => {
        playerRef.current?.play();
    }, []);

    const stop = useCallback(() => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.pause();
        p.currentTime(0);
        hasStartedRef.current = false;
        isEndedRef.current = false;
        setHasStarted(false);
        setIsEnded(false);
        setIsPlaying(false);
        setCurrentTime(0);
    }, []);

    return {
        containerRef,
        videoRef,
        hasStarted,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        qualities,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleVolumeChange,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings: () => setShowSettings((v) => !v),
        getCurrentTime,
        play,
        stop
    };
};

export default useVideoPlayer;
